import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { UNITS_OF_MEASURE } from "../../../constants/inventory";
import { IoArrowBack } from "react-icons/io5";
import { Skeleton } from "@mui/material";

const ViewStockItem = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const { id } = useParams();

  const [item, setItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    fetchItem();
    fetchHistory();
  }, [id]);

  const fetchItem = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/getInventoryItem/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setItem(data.item);
      } else {
        setErrorMessage(data.error || "Failed to fetch item");
        setTimeout(() => setErrorMessage(""), 3000);
      }
    } catch (err) {
      setErrorMessage("Failed to fetch item");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await fetch(`/api/stockHistory/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setHistory(data.adjustments || data.history || []);
      }
    } catch (err) {
      // History may not be available for all items
    } finally {
      setHistoryLoading(false);
    }
  };

  const getUnitLabel = (unitId) => {
    const unit = UNITS_OF_MEASURE.find((u) => u.id === unitId);
    return unit ? unit.name : unitId;
  };

  const getStockStatusBadge = () => {
    if (!item) return null;
    if (item.minThreshold > 0 && item.currentStock <= item.minThreshold) {
      return (
        <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
          Low Stock
        </span>
      );
    }
    if (item.maxThreshold > 0 && item.currentStock >= item.maxThreshold) {
      return (
        <span className="px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
          Overstocked
        </span>
      );
    }
    return (
      <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
        In Stock
      </span>
    );
  };

  const getMovementBadge = (type) => {
    const colors = {
      in: "bg-green-100 text-green-800",
      out: "bg-red-100 text-red-800",
      adjustment: "bg-blue-100 text-blue-800",
      return: "bg-yellow-100 text-yellow-800",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
          colors[type] || "bg-gray-100 text-gray-800"
        }`}
      >
        {type}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="m-2 md:m-6 p-2 md:p-6">
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="m-2 md:m-6 p-2 md:p-6 text-center">
        <p className="text-gray-500">Item not found</p>
        <button
          onClick={() => navigate("/StockMaster")}
          className="mt-4 text-primary hover:underline"
        >
          Back to Stock Master
        </button>
      </div>
    );
  }

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/StockMaster")}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <IoArrowBack className="text-xl text-primary" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-gray-800">{item.name}</h2>
            <p className="text-sm text-gray-500">SKU: {item.sku}{item.itemCode ? ` | Item Code: ${item.itemCode}` : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">{getStockStatusBadge()}</div>
      </div>

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {errorMessage}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Item Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
              Basic Information
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Item Name</p>
                <p className="font-medium">{item.name}</p>
              </div>
              <div>
                <p className="text-gray-500">SKU</p>
                <p className="font-medium text-primary">{item.sku}</p>
              </div>
              <div>
                <p className="text-gray-500">Item Code</p>
                <p className="font-medium text-primary">{item.itemCode || "N/A"}</p>
              </div>
              <div>
                <p className="text-gray-500">Category</p>
                <p className="font-medium">{item.category?.name || "N/A"}</p>
              </div>
              <div>
                <p className="text-gray-500">Unit of Measure</p>
                <p className="font-medium">{getUnitLabel(item.unitOfMeasure)}</p>
              </div>
            </div>
          </div>

          {/* Stock & Pricing */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
              Stock & Pricing
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Current Stock</p>
                <p className="font-medium text-lg">{item.currentStock}</p>
              </div>
              <div>
                <p className="text-gray-500">Unit Cost</p>
                <p className="font-medium">Nu. {item.unitCost?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-gray-500">Total Value</p>
                <p className="font-medium text-primary">
                  Nu. {(item.currentStock * item.unitCost)?.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Barcode</p>
                <p className="font-medium">{item.barcode || "N/A"}</p>
              </div>
            </div>
          </div>

          {/* Thresholds */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
              Thresholds & Alerts
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Min Threshold</p>
                <p className="font-medium">{item.minThreshold || 0}</p>
              </div>
              <div>
                <p className="text-gray-500">Max Threshold</p>
                <p className="font-medium">{item.maxThreshold || 0}</p>
              </div>
              <div>
                <p className="text-gray-500">Reorder Point</p>
                <p className="font-medium">{item.reorderPoint || 0}</p>
              </div>
            </div>
          </div>

          {/* Properties */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
              Item Properties
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Location / Shelf</p>
                <p className="font-medium">{item.location || "N/A"}</p>
              </div>
              <div>
                <p className="text-gray-500">Serialized Asset</p>
                <p className="font-medium">{item.isSerialized ? "Yes" : "No"}</p>
              </div>
              <div>
                <p className="text-gray-500">Returnable</p>
                <p className="font-medium">{item.isReturnable !== false ? "Yes" : "No"}</p>
              </div>
            </div>
          </div>

          {/* Description */}
          {item.description && (
            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">
                Description
              </h3>
              <p className="text-sm text-gray-700">{item.description}</p>
            </div>
          )}
        </div>

        {/* Right: Stock Movement History */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">
              Stock Movement History
            </h3>
            {historyLoading ? (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} variant="rectangular" height={50} sx={{ borderRadius: 1 }} />
                ))}
              </div>
            ) : history.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">
                No movement history found
              </p>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {history.map((entry, index) => (
                  <div
                    key={entry._id || index}
                    className="border border-gray-100 rounded-lg p-3 text-sm"
                  >
                    <div className="flex items-center justify-between mb-1">
                      {getMovementBadge(entry.adjustmentType || entry.type)}
                      <span className="text-xs text-gray-400">
                        {new Date(entry.date || entry.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-gray-600">
                        Qty: <span className="font-medium">{entry.adjustedQty || entry.quantity}</span>
                      </span>
                      {(entry.newQty !== undefined || entry.balanceAfter !== undefined) && (
                        <span className="text-gray-500 text-xs">
                          Balance: {entry.newQty ?? entry.balanceAfter}
                        </span>
                      )}
                    </div>
                    {(entry.adjustmentNumber || entry.reference) && (
                      <p className="text-xs text-gray-400 mt-1">Ref: {entry.adjustmentNumber || entry.reference}</p>
                    )}
                    {(entry.reason || entry.notes) && (
                      <p className="text-xs text-gray-500 mt-1">{entry.reason || entry.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Info Card */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
              Record Info
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Created</span>
                <span className="font-medium">
                  {item.createdAt
                    ? new Date(item.createdAt).toLocaleDateString()
                    : "N/A"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Last Updated</span>
                <span className="font-medium">
                  {item.updatedAt
                    ? new Date(item.updatedAt).toLocaleDateString()
                    : "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewStockItem;
